import { useCallback, useRef } from 'react';
import { useFile, useVisualizer, useUI } from '../context/AppContext.jsx';
import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';
import { extractNodes } from '../utils/nodeUtils.js';

export function useFileHandlers() {
  const {
    parser,
    installationParser,
    currentFileData,
    currentWaypoints,
    currentFeatures,
    nodes,
    setCurrentFileData,
    setCurrentWaypoints,
    setCurrentFeatures,
    setNodes,
    setCurrentFileName,
    setCurrentInstallationFileName,
    setCurrentInstallationData,
  } = useFile();
  const { visualizer } = useVisualizer();
  const { showStatus, setIsLoading, setCurrentRobotType } = useUI();
  
  // Track pending visualization timeouts to cancel them when loading new files
  const visualizationTimeoutRef = useRef(null);

  const detectRobotType = useCallback((serialNumber) => {
    if (!serialNumber || serialNumber === 'Unknown') {
      return 'ur10e';
    }
    const digits = serialNumber.replace(/\D/g, '');
    if (digits.length < 6) {
      return 'ur10e';
    }
    const fifthSixth = digits.substring(4, 6);
    if (fifthSixth === '52') {
      return 'ur10e';
    } else if (fifthSixth === '68') {
      return 'ur20';
    }
    return 'ur10e';
  }, []);

  const handleFile = useCallback(async (file) => {
    try {
      // Cancel any pending visualization from previous file loads
      if (visualizationTimeoutRef.current) {
        clearTimeout(visualizationTimeoutRef.current);
        visualizationTimeoutRef.current = null;
      }

      showStatus('Loading file...', 'info');
      setIsLoading(true);

      const fileData = await parser.parseFile(file);
      setCurrentFileData(fileData);
      setCurrentWaypoints(fileData.waypoints);
      setCurrentFileName(file.name);

      // Detect robot type and auto-load the model
      const robotType = detectRobotType(fileData.robotSerial);
      setCurrentRobotType(robotType);
      if (robotType && visualizer) {
        await visualizer.addRobotModel(robotType);
        visualizer.setRobotVisible(true);
      }

      // Extract nodes
      const extractedNodes = extractNodes(fileData.waypoints);
      setNodes(extractedNodes);

      // Visualize waypoints after a brief delay to ensure visualizer is ready
      visualizationTimeoutRef.current = setTimeout(() => {
        visualizationTimeoutRef.current = null;
        // Get visualizer from window to ensure we have the latest instance
        const viz = window.visualizer || visualizer;
        if (viz) {
          // Use setCurrentFeatures callback form to get latest features
          setCurrentFeatures(latestFeatures => {
            const waypointsForVis = convertWaypointsToWorldCoords(fileData.waypoints, latestFeatures);
            // Only center camera if features are loaded, otherwise wait for installation file
            const shouldCenter = latestFeatures && latestFeatures.length > 1; // More than just Base
            viz.visualizeWaypoints(waypointsForVis, shouldCenter);
            return latestFeatures; // Return unchanged
          });
          showStatus(`Loaded ${fileData.waypointCount} waypoints from ${fileData.fileName}`, 'success');
          setIsLoading(false);
        } else {
          // Retry after a longer delay
          visualizationTimeoutRef.current = setTimeout(() => {
            visualizationTimeoutRef.current = null;
            const viz = window.visualizer || visualizer;
            if (viz) {
              // Use setCurrentFeatures callback form to get latest features
              setCurrentFeatures(latestFeatures => {
                const waypointsForVis = convertWaypointsToWorldCoords(fileData.waypoints, latestFeatures);
                // Only center camera if features are loaded
                const shouldCenter = latestFeatures && latestFeatures.length > 1;
                viz.visualizeWaypoints(waypointsForVis, shouldCenter);
                return latestFeatures; // Return unchanged
              });
              showStatus(`Loaded ${fileData.waypointCount} waypoints from ${fileData.fileName}`, 'success');
              setIsLoading(false);
            } else {
              showStatus('Error: Visualizer not initialized', 'error');
              setIsLoading(false);
            }
          }, 500);
        }
      }, 100);
    } catch (error) {
      console.error('Error handling file:', error);
      showStatus(`Error: ${error.message}`, 'error');
      setIsLoading(false);
    }
  }, [parser, visualizer, detectRobotType, extractNodes, setCurrentFileData, setCurrentWaypoints, setCurrentFileName, setCurrentFeatures, setNodes, showStatus, setIsLoading, setCurrentRobotType]);

  const handleInstallationFile = useCallback(async (file, waypointsToUse = null) => {
    try {
      showStatus('Loading installation file...', 'info');
      setIsLoading(true);

      const arrayBuffer = await file.arrayBuffer();
      setCurrentInstallationData(new Uint8Array(arrayBuffer));
      setCurrentInstallationFileName(file.name);

      const installationData = await installationParser.parseFile(file);
      setCurrentFeatures(installationData.features);

      // Update visualization with features
      if (visualizer) {
        visualizer.visualizeFeatures(installationData.features);
        // Re-visualize waypoints with updated features
        // Use explicitly passed waypoints or fall back to currentWaypoints
        const waypoints = waypointsToUse || currentWaypoints;
        if (waypoints.length > 0) {
          const waypointsForVis = convertWaypointsToWorldCoords(waypoints, installationData.features);
          // Center camera now that we have proper world coordinates
          visualizer.visualizeWaypoints(waypointsForVis, true);
        }
      }

      showStatus(`Loaded ${installationData.featureCount} features`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error handling installation file:', error);
      showStatus(`Error: ${error.message}`, 'error');
      setIsLoading(false);
    }
  }, [installationParser, visualizer, currentWaypoints, setCurrentInstallationData, setCurrentInstallationFileName, setCurrentFeatures, showStatus, setIsLoading]);

  // Generic test data loader - can load any URP file with an installation file
  const loadTestDataFile = useCallback(async (urpFileName, installationFileName = 'default.installation') => {
    try {
      setIsLoading(true);
      const isLargeFile = urpFileName.includes('200-');
      showStatus(isLargeFile ? 'Loading large sample files...' : 'Loading sample files...', 'info');

      // Load the .urp file
      const urpResponse = await fetch(`/${urpFileName}`);
      if (!urpResponse.ok) {
        throw new Error('Failed to load sample program file');
      }
      const urpBlob = await urpResponse.blob();
      const urpFile = new File([urpBlob], urpFileName, { type: 'application/octet-stream' });

      // Parse the file to get waypoints
      const fileData = await parser.parseFile(urpFile);

      // Load the .installation file
      const installationResponse = await fetch(`/${installationFileName}`);
      if (!installationResponse.ok) {
        throw new Error('Failed to load sample installation file');
      }
      const installationBlob = await installationResponse.blob();
      const installationFile = new File([installationBlob], installationFileName, { type: 'application/octet-stream' });

      // Load both files - pass waypoints explicitly to avoid stale state
      await handleFile(urpFile);

      // Parse installation file
      const arrayBuffer = await installationFile.arrayBuffer();
      setCurrentInstallationData(new Uint8Array(arrayBuffer));
      setCurrentInstallationFileName(installationFile.name);
      const installationData = await installationParser.parseFile(installationFile);
      setCurrentFeatures(installationData.features);

      // Update visualization with features - wait for handleFile's visualization to complete
      await new Promise(resolve => setTimeout(resolve, 700));
      if (visualizer) {
        visualizer.visualizeFeatures(installationData.features);
        const waypointsForVis = convertWaypointsToWorldCoords(fileData.waypoints, installationData.features);
        // Don't center camera again - handleFile already did that
        visualizer.visualizeWaypoints(waypointsForVis, false);
      }

      showStatus(isLargeFile ? 'Large sample files loaded successfully' : 'Sample files loaded successfully', 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading test data:', error);
      showStatus(`Error loading sample files: ${error.message}`, 'error');
      setIsLoading(false);
    }
  }, [parser, installationParser, visualizer, handleFile, setCurrentInstallationData, setCurrentInstallationFileName, setCurrentFeatures, showStatus, setIsLoading]);

  // Convenience wrappers for specific test files
  const loadTestData = useCallback(() => loadTestDataFile('editor_test.urp'), [loadTestDataFile]);
  const loadTestData2 = useCallback(() => loadTestDataFile('200-15043.urp'), [loadTestDataFile]);

  return {
    handleFile,
    handleInstallationFile,
    loadTestData,
    loadTestData2,
    detectRobotType,
  };
}

